/**
 * Phép tính thuần của màn Quản lý nhiệm vụ, chuyển nguyên từ `taskMeta.ts` và `DecomposeDialog.tsx` của
 * foxainative: phạm vi → vai trò lọc, hạn xử lý theo ngày lịch, tìm nút trong cây, khớp tên đơn vị
 * AI chọn với cơ cấu tổ chức. Không chạm DOM, không gọi HTTP.
 */
import type {
  DonViDanhBa,
  GoiYViecCon,
  LocTrangThai,
  LocVaiTro,
  NguoiDanhBa,
  NguoiThamGiaVao,
  NutNhiemVu,
  PhamViNhiemVu,
  TrangThaiNhiemVu,
  VaiTroThamGia,
} from '../types-nhiem-vu.ts';

export const TRANG_THAI_NHIEM_VU: TrangThaiNhiemVu[] = ['MOI_PHAN_CONG', 'DANG_THUC_HIEN', 'HOAN_THANH'];
export const LOC_TRANG_THAI: LocTrangThai[] = [
  'MOI_PHAN_CONG',
  'DANG_THUC_HIEN',
  'SAP_DEN_HAN',
  'QUA_HAN',
  'HOAN_THANH',
];
export const VAI_TRO_THAM_GIA: VaiTroThamGia[] = ['CHU_TRI', 'PHOI_HOP', 'XEM_DE_BIET'];
export const PHAM_VI: PhamViNhiemVu[] = ['mine', 'assigned', 'all'];

export const TOI_DA_TEP_MB = 20;
export const BYTE_MOI_MB = 1024 * 1024;
export const CO_TRANG = 20;
export const SO_AVATAR = 3;
export const SAP_DEN_HAN_NGAY = 3;
export const DINH_DANG_NGAY = 'DD/MM/YYYY';
export const DINH_DANG_API = 'YYYY-MM-DD';

export function vaiTroLoc(phamVi: PhamViNhiemVu, vaiTroCon: VaiTroThamGia | ''): LocVaiTro | undefined {
  if (phamVi === 'assigned') return 'GIAO_VIEC';
  if (phamVi === 'mine') return vaiTroCon || 'THAM_GIA';
  return undefined;
}

/** Quá hạn hiện như một trạng thái riêng, dù server lưu là chưa xong. */
export function trangThaiHien(nv: Pick<NutNhiemVu, 'status' | 'is_overdue'>): LocTrangThai {
  return nv.is_overdue ? 'QUA_HAN' : nv.status;
}

export function chuCaiDau(ten: string): string {
  const tu = ten.trim().split(/\s+/).filter(Boolean);
  const dau = tu[0]?.[0] ?? '?';
  const cuoi = tu.length > 1 ? (tu[tu.length - 1]?.[0] ?? '') : '';
  return (dau + cuoi).toUpperCase();
}

/** Chỉ số ổn định theo tên, để cùng một người luôn cùng một màu avatar. */
export function chiSoTheoTen(ten: string, so: number): number {
  let hash = 0;
  for (const ch of ten) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return hash % so;
}

export function phanTram(phan: number, tong: number): number {
  return tong > 0 ? Math.round((phan / tong) * 100) : 0;
}

export type LoaiHan = 'overdue' | 'today' | 'soon' | 'later' | 'done' | 'none';

const MS_MOT_NGAY = 86_400_000;

/** So theo ngày lịch địa phương, không theo giờ — "hôm nay" là cả ngày hôm nay. */
export function thongTinHan(
  nv: Pick<NutNhiemVu, 'deadline' | 'status'>,
  bayGio = new Date(),
): { kind: LoaiHan; days: number } {
  if (!nv.deadline) return { kind: 'none', days: 0 };
  if (nv.status === 'HOAN_THANH') return { kind: 'done', days: 0 };
  const [y, m, d] = nv.deadline.split('-').map(Number);
  const han = Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1);
  const homNay = Date.UTC(bayGio.getFullYear(), bayGio.getMonth(), bayGio.getDate());
  const days = Math.round((han - homNay) / MS_MOT_NGAY);
  if (days < 0) return { kind: 'overdue', days: -days };
  if (days === 0) return { kind: 'today', days };
  if (days <= SAP_DEN_HAN_NGAY) return { kind: 'soon', days };
  return { kind: 'later', days };
}

export function demHauDue(nv: NutNhiemVu): number {
  return nv.children.reduce((n, c) => n + 1 + demHauDue(c), 0);
}

export function timNut(
  ds: NutNhiemVu[],
  id: string,
  cha: NutNhiemVu | null = null,
): { task: NutNhiemVu; parent: NutNhiemVu | null } | null {
  for (const nut of ds) {
    if (nut.id === id) return { task: nut, parent: cha };
    const thay = timNut(nut.children, id, nut);
    if (thay) return thay;
  }
  return null;
}

/** Trùng tên trước, rồi chứa nhau — như `matchOrg` của bản cũ. */
export function khopDonVi(ten: string, ds: DonViDanhBa[]): string {
  const can = ten.trim().toLocaleLowerCase('vi');
  if (!can) return '';
  const dung = ds.find((o) => o.name.toLocaleLowerCase('vi') === can);
  const gan = ds.find((o) => {
    const n = o.name.toLocaleLowerCase('vi');
    return n.includes(can) || can.includes(n);
  });
  return (dung ?? gan)?.id ?? '';
}

/** Giao cho cá nhân: đơn vị chủ trì là đơn vị trực thuộc của người đó. */
export function donViAiChon(g: GoiYViecCon): string {
  if (!g.assignee_type) return '';
  return g.assignee_type === 'CA_NHAN' ? (g.assignee_parent_name ?? '') : g.assignee_name;
}

/** Chỉ nhận khi trùng TÊN ĐẦY ĐỦ và duy nhất — hai người cùng tên thì để người dùng chọn. */
export function nguoiTrungTen(ten: string, ds: NguoiDanhBa[]): NguoiDanhBa | null {
  const can = ten.trim().toLocaleLowerCase('vi');
  const trung = ds.filter((u) => u.full_name.trim().toLocaleLowerCase('vi') === can);
  return trung.length === 1 ? (trung[0] ?? null) : null;
}

export function thanhNguoiThamGia(
  u: NguoiDanhBa,
  dsDonVi: DonViDanhBa[],
  role: VaiTroThamGia,
): NguoiThamGiaVao {
  const dv = dsDonVi.find((o) => u.organization_ids.includes(o.id));
  return {
    user_id: u.id,
    user_name: u.full_name,
    user_email: u.email,
    org_id: dv?.id ?? null,
    org_name: dv?.name ?? null,
    role,
  };
}

/** Đọc JSON của dòng thời gian; dữ liệu hỏng thì `null` thay vì làm vỡ cả danh sách. */
export function docJson<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
